import React from "react";
import { PenLine, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { endpoints } from "@/lib/api";
import { toast } from "sonner";

const fmt = (s) => (s ? new Date(s).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "—");

const Row = ({ d, onOpen }) => (
  <tr data-testid={`wd-row-${d.id}`} className="border-t border-slate-100 dark:border-white/5 hover:bg-slate-50/60 dark:hover:bg-white/5">
    <td className="px-4 py-3 font-mono text-xs font-semibold">{d.doc_number}</td>
    <td className="px-4 py-3">
      <button data-testid={`wd-open-${d.id}`} onClick={() => onOpen(d.id)} className="text-left font-medium hover:text-indigo-600">{d.title}</button>
      <div className="text-[10px] text-slate-500">{d.client_name} · {d.doc_type}</div>
    </td>
    <td className="px-4 py-3 text-center font-mono text-xs font-semibold">{d.revision || "—"}</td>
    <td className="px-4 py-3 text-xs">{d.authorised_by || "—"}</td>
    <td className="px-4 py-3 text-xs">{fmt(d.updated_at)}</td>
    <td className="px-4 py-3 text-right whitespace-nowrap">
      <Button size="sm" variant="outline" className="mr-1" data-testid={`wd-export-${d.id}`}
        onClick={() => endpoints.exportWrittenDoc(d.id).catch(() => toast.error("Could not export"))}>
        <FileDown className="h-3.5 w-3.5 mr-1" /> Word
      </Button>
      <Button size="sm" variant="outline" data-testid={`wd-edit-${d.id}`} onClick={() => onOpen(d.id)}>Edit</Button>
    </td>
  </tr>
);

export const WrittenDocsSection = ({ docs, onNew, onOpen }) => (
  <div data-testid="written-docs-section" className="glass-card rounded-3xl overflow-hidden mt-6">
    <div className="p-4 sm:p-5 flex flex-wrap items-center gap-3">
      <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center shadow-md shadow-indigo-500/25">
        <PenLine className="h-5 w-5 text-white" />
      </div>
      <div>
        <h2 className="font-display text-base md:text-lg font-extrabold tracking-tight">Written Procedures & Policies</h2>
        <p className="text-xs text-slate-500">Write directly in the portal and export a branded Word document</p>
      </div>
      <Button data-testid="wd-new-button" size="sm" className="ml-auto brand-gradient text-white" onClick={onNew}>
        <PenLine className="h-4 w-4 mr-1" /> Write document
      </Button>
    </div>
    {docs.length === 0 ? (
      <div data-testid="wd-empty" className="px-5 pb-6 text-sm text-slate-500">No written procedures or policies yet. Click "Write document" to start one.</div>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50/70 dark:bg-slate-900/60 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
            <tr>
              <th className="text-left px-4 py-3">Doc #</th><th className="text-left px-4 py-3">Name</th>
              <th className="text-center px-4 py-3">Rev</th><th className="text-left px-4 py-3">Authorised by</th>
              <th className="text-left px-4 py-3">Last edited</th><th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>{docs.map((d) => <Row key={d.id} d={d} onOpen={onOpen} />)}</tbody>
        </table>
      </div>
    )}
  </div>
);
